/**
 * ReadingLayout — a centered reading column for long-form / reading surfaces.
 *
 * Centers the body at a comfortable reading measure (like the rest of the app)
 * instead of letting it hug the gutter. Pure layout, server-safe.
 *
 * NOTE: page-contextual NAVIGATION (a doc's sibling list / on-this-page TOC) does
 * NOT belong inside this column — it floats in the right-hand gutter well via
 * `ContextRail` (a page pushes a `side: "right"` rail extension with
 * `useProvideRailExtension`), mirroring how the modes float on the left. This
 * block is just the centered body; see `excellent/app/_chrome/context-rail.tsx`.
 */

import * as React from "react";
import { cn } from "../utils";

export interface ReadingLayoutProps {
  /** The reading content (header + body). Centered at `measure`. */
  children: React.ReactNode;
  /**
   * The reading measure — the max width of the centered column. Any CSS length;
   * applied as an inline `max-width` so it dodges Tailwind's static-class scan.
   * Defaults to the app's standard prose measure (44rem).
   */
  measure?: string;
  /** Extra classes on the outer wrapper (e.g. vertical padding). */
  className?: string;
}

const DEFAULT_MEASURE = "44rem";

export function ReadingLayout({
  children,
  measure = DEFAULT_MEASURE,
  className,
}: ReadingLayoutProps) {
  return (
    <div className={cn("mx-auto", className)} style={{ maxWidth: measure }}>
      {children}
    </div>
  );
}
